import { OrganizationMemberWithOrganization, User } from "@models";
import * as Sentry from "@sentry/react";
import { Crisp } from "crisp-sdk-web";
import { PostHog } from "posthog-js/react";

type UserNameFields = {
  firstName?: string | null;
  lastName?: string | null;
};

export type UserWithOrganizationMemberships<
  TOrganizationMembership extends { organizationId?: string | null } = OrganizationMemberWithOrganization,
> = {
  organizationMemberships?: TOrganizationMembership[] | null;
};

export const normalizeNullableUserNameFields = <T extends UserNameFields>(
  user: T
): Omit<T, "firstName" | "lastName"> & {
  firstName: string;
  lastName: string;
} => ({
  ...user,
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
});

export const getUserDisplayName = ({
  firstName,
  lastName,
}: UserNameFields): string => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  return fullName || "Unknown user";
};

export const getOrgMembership = <
  TOrganizationMembership extends { organizationId?: string | null },
>(
  user: UserWithOrganizationMemberships<TOrganizationMembership> | undefined,
  orgId?: string
): TOrganizationMembership | undefined => {
  if (!user || !orgId) return undefined;

  return user.organizationMemberships?.find(
    (membership) => membership.organizationId === orgId
  );
};

export const getOrgFromUser = (user?: User, orgId?: string) => {
  const orgMember = getOrgMembership(user, orgId);
  return orgMember?.organization;
};

export const getUserHomeOrg = (user: User) => {
  const orgMember = user.organizationMemberships.find((o) => o.homeOrg);
  return orgMember?.organization;
};

export const setTracingContext = (user: User, postHog?: PostHog) => {
  Crisp.user.setEmail(user.email);
  Crisp.user.setNickname(user.firstName + " " + user.lastName);
  Sentry.setUser({
    email: user.email,
    name: user.firstName + " " + user.lastName,
  });
  if (postHog) {
    postHog.identify(user.id, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    });
  }
};
